import request from '@/utils/request';
import type {
  CreateUserData,
  PointsRecord,
  PointsRecordParams,
  UpdateUserData,
  User,
  UserQueryParams
} from '@/types/user';
import type {PaginatedResponse} from '@/types/common';

export const userService = {
  getUsers: async (params: UserQueryParams): Promise<PaginatedResponse<User>> => {
    const response = await request.get<PaginatedResponse<User>>('/api/admin/sysusers', { params });
    return response;
  },

  getUserById: async (id: string): Promise<User> => {
    const response = await request.get<User>(`/api/admin/sysusers/${id}`);
    return response;
  },

  createUser: async (data: CreateUserData): Promise<User> => {
    const response = await request.post<User>('/api/admin/sysusers', data);
    return response;
  },

  updateUser: async (id: string, data: UpdateUserData): Promise<User> => {
    const response = await request.put<User>(`/api/admin/sysusers/${id}`, data);
    return response;
  },

  deleteUser: async (id: string): Promise<void> => {
    await request.delete(`/api/admin/sysusers/${id}`);
  },

  assignRoles: async (id: string, roleCodes: string[]): Promise<void> => {
    await request.put(`/api/admin/sysusers/${id}/roles`, roleCodes);
  },

  changePassword: async (id: string, oldPassword: string, newPassword: string): Promise<void> => {
    await request.put(`/api/admin/sysusers/${id}/password`, null, {
      params: {
        oldPassword,
        newPassword
      }
    });
  },

  resetPassword: async (id: string): Promise<void> => {
    await request.put(`/api/admin/sysusers/${id}/password/reset`);
  },

  getCUsers: async (params: UserQueryParams): Promise<PaginatedResponse<User>> => {
    const response = await request.get<PaginatedResponse<User>>('/api/admin/users', { params });
    return response;
  },

  getCUserById: async (id: string): Promise<User> => {
    const response = await request.get<User>(`/api/admin/users/${id}`);
    return response;
  },
  getPointsRecords: async (params: PointsRecordParams): Promise<PaginatedResponse<PointsRecord>> => {
    const response = await request.get<PaginatedResponse<PointsRecord>>('/api/admin/users/points-records', { params });
    return response;
  },

  manageBlacklist: async (userId: number, addToBlacklist: boolean, reason?: string): Promise<void> => {
    await request.post('/api/admin/users/blacklist', {
      userId,
      addToBlacklist,
      reason
    });
  },

  manageWhitelist: async (userId: number, addToWhitelist: boolean): Promise<void> => {
    await request.post('/api/admin/users/whitelist', {
      userId,
      addToWhitelist
    });
  },

  importWhitelistUsers: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await request.post('/api/admin/users/whitelist/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response;
  },

  clearIdentityInfo: async (userId: number): Promise<void> => {
    await request.post('/api/admin/users/clear-identity-info', { userId });
  },

  downloadWhitelistImportTemplate: async (): Promise<void> => {
    const response = await request.get('/api/admin/users/whitelist/import/template', {
      responseType: 'blob'
    });
    
    // 创建下载链接
    const url = window.URL.createObjectURL(new Blob([response], { 
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
    }));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', '白名单用户导入模板.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

};
